import { ContactInfoEditorViewModel } from '@/features/tax-profile/ContactInfoEditorViewModel'
import { PersonalInfoEditorViewModel } from '@/features/tax-profile/individual-profiles/PersonalInfoEditorViewModel'
import { action, observable, runInAction, computed } from 'mobx'
import type { PersonalProfileVersion } from '@/features/tax-profile/individual-profiles/PersonalProfile'
import { required, validationContext } from 'validx'
import {
  mustBeValidTaxFormInputValue,
  mustHaveMaxLength,
  validateObject,
} from '@/utils/validx-util'
import type { IndividualProfileStore } from '@/features/tax-profile/individual-profiles/IndividualProfileStore'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { task } from 'mobx-task'
import { toAbortSignal } from '@/utils/rx-util'
import type { GetCurrentMemberFn } from '@/features/iam'
import type { SnackbarErrorHandlerFactory } from '@/features/shell'

export interface PersonalProfileEditorViewModelOptions {
  /**
   * Whether the editor is in read-only mode.
   */
  isReadOnly?: () => boolean

  /**
   * Called when the profile has been saved.
   *
   * @param profile
   */
  onSaved(profile: PersonalProfileVersion): Promise<unknown>
}

/**
 * Editor for a personal profile.
 */
export class PersonalProfileEditorViewModel {
  readonly personalInfoVm = new PersonalInfoEditorViewModel()
  readonly contactInfoVm = new ContactInfoEditorViewModel()

  /**
   * Underlying modal state, for when the editor is displayed in a modal.
   */
  readonly modal = new ModalViewModel()

  @observable accessor occupation = ''
  @observable accessor isNew = true
  @observable accessor isPrefilledUsingLatest = false

  /**
   * Whether the editor is in read-only mode.
   */
  @computed get isReadOnly() {
    return this._options.isReadOnly?.() ?? false
  }

  readonly validation = validationContext(this, {
    personalInfoVm: [validateObject()],
    contactInfoVm: [validateObject()],
    occupation: [
      required('Occupation is required'),
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(100, 'Occupation must not exceed 100 characters'),
    ],
  })

  constructor(
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _individualProfileStore: IndividualProfileStore,
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
    private readonly _options: PersonalProfileEditorViewModelOptions,
  ) {}

  /**
   * Shows the dialog.
   */
  @action.bound
  showDialog(personalProfile: PersonalProfileVersion | null) {
    this.fetchFallback.reset()
    this.reset(personalProfile)

    if (!personalProfile) {
      const { signal } = toAbortSignal(this.modal.close$)
      void this.fetchFallback(signal)
    }

    this.modal.show()
  }

  /**
   * Closes the modal.
   */
  @action.bound
  close() {
    this.modal.close()
  }

  /**
   * Resets the state of the view model to that of the given
   * {@link PersonalProfileVersion}.
   */
  @action.bound
  reset(profile?: PersonalProfileVersion | null) {
    this.isNew = !profile
    this.isPrefilledUsingLatest = false // resets the flag
    this.personalInfoVm.reset(profile?.personalInfo)
    this.contactInfoVm.reset(profile?.contactInfo)
    this.occupation = profile?.occupation ?? ''

    this.validation.reset()
  }

  /**
   * Fetches the latest profile to suggest as a fallback, or uses the
   * info from the currently authenticated user.
   */
  fetchFallback = task.resolved(async (signal?: AbortSignal) => {
    const latest =
      await this._individualProfileStore.fetchLatestPersonalProfileVersion(
        signal,
      )
    signal?.throwIfAborted()
    runInAction(() => {
      this.reset(latest)

      // Treat it as a new profile.
      this.isNew = true

      // Track whether we prefilled using the latest profile.
      this.isPrefilledUsingLatest = Boolean(latest)

      if (!latest) {
        // We have no formal info recorded, fall back to info
        // on the current user.
        const user = this._getCurrentMember().user
        this.personalInfoVm.firstName = user.givenName
        this.personalInfoVm.lastName = user.familyName
        this.contactInfoVm.email = user.email ?? ''
        this.contactInfoVm.phoneNumber = user.phone ?? ''
      }
    })
  })

  /**
   * Saves the profile and closes the dialog.
   */
  save = task.resolved(async () => {
    this.validation.reset().validate()
    if (!this.validation.isValid) {
      return
    }

    await this._individualProfileStore
      .savePersonalProfileVersion({
        personalInfo: this.personalInfoVm.toPersonalInfo(),
        contactInfo: this.contactInfoVm.toContactInfo(),
        occupation: this.occupation,
      })
      .then(this._options.onSaved)
      .then(() => this.close())
      .catch(this._createSnackbarErrorHandler('Failed to save profile'))
  })
}
